/** Billing settings (bank, QR-bill, payment terms) and VAT rates. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { Select } from "@primereact/ui/select";
import { apiDelete, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import {
  ConfirmButton,
  ErrorPanel,
  LoadingPanel,
  MutationError,
  Section,
} from "../shared.js";

export type Billing = {
  currency: string;
  vatRegistered: boolean;
  paymentTermDays: number;
  bankName?: string;
  iban?: string;
  referenceType: string;
  defaultInvoiceMessage?: string;
  roundingStep: string;
};

export type TaxRate = {
  id: string;
  name: string;
  rate: string;
  isDefault: boolean;
  active: boolean;
};

export const DEFAULT_TAX_RATE_KEYS: Record<string, string> = {
  "Standard rate": "standard",
  "Reduced rate": "reduced",
  "Zero rate": "zero",
};

export function BillingForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["settings", "billing"],
    queryFn: () =>
      apiGet<{
        data: Billing;
      }>("/settings/billing").then((response) => response.data),
  });
  const [form, setForm] = useState<Billing | null>(null);
  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);
  const save = useMutation({
    mutationFn: () => apiPatch("/settings/billing", form),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["settings", "billing"],
      });
    },
  });
  if (query.isLoading || !form) return <LoadingPanel />;
  if (query.error) return <ErrorPanel error={query.error} />;
  return (
    <div className="space-y-5">
      <Section
        title={t("settings.billing", {
          defaultValue: "Billing",
        })}
      >
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <Field
            label={t("settings.currency", {
              defaultValue: "Currency",
            })}
          >
            <Select.Root
              value={form.currency}
              onValueChange={(event: { value: unknown }) =>
                setForm({
                  ...form,
                  currency: event.value as string,
                })
              }
              fluid
              options={[
                { value: "CHF", label: "CHF" },
                { value: "EUR", label: "EUR" },
              ]}
              optionLabel="label"
              optionValue="value"
            >
              <Select.Trigger type="button">
                <Select.Value />
                <Select.Indicator className="pi pi-chevron-down" />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner>
                  <Select.Popup>
                    <Select.List />
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </Field>
          <Field
            label={t("settings.paymentDays", {
              defaultValue: "Payment term days",
            })}
          >
            <InputNumber.Root
              fluid
              min={0}
              value={form.paymentTermDays}
              onValueChange={(event: { value: number | null }) =>
                setForm({
                  ...form,
                  paymentTermDays: event.value ?? 0,
                })
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
          <Field
            label={t("settings.bank", {
              defaultValue: "Bank name",
            })}
          >
            <Input
              value={form.bankName ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setForm({
                  ...form,
                  bankName: event.target.value,
                })
              }
            />
          </Field>
          <Field label="IBAN">
            <Input
              value={form.iban ?? ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setForm({
                  ...form,
                  iban: event.target.value,
                })
              }
            />
          </Field>
          <Field
            label={t("settings.referenceType", {
              defaultValue: "QR reference type",
            })}
          >
            <Select.Root
              value={form.referenceType}
              onValueChange={(event: { value: unknown }) =>
                setForm({
                  ...form,
                  referenceType: event.value as string,
                })
              }
              fluid
              options={[
                { value: "NON", label: "NON" },
                { value: "SCOR", label: "SCOR" },
                { value: "QRR", label: "QRR" },
              ]}
              optionLabel="label"
              optionValue="value"
            >
              <Select.Trigger type="button">
                <Select.Value />
                <Select.Indicator className="pi pi-chevron-down" />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner>
                  <Select.Popup>
                    <Select.List />
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </Field>
          <Field
            label={t("settings.rounding", {
              defaultValue: "Rounding step",
            })}
          >
            <Select.Root
              value={form.roundingStep}
              onValueChange={(event: { value: unknown }) =>
                setForm({
                  ...form,
                  roundingStep: event.value as string,
                })
              }
              fluid
              options={[
                { value: "0.01", label: "0.01" },
                { value: "0.05", label: "0.05" },
              ]}
              optionLabel="label"
              optionValue="value"
            >
              <Select.Trigger type="button">
                <Select.Value />
                <Select.Indicator className="pi pi-chevron-down" />
              </Select.Trigger>
              <Select.Portal>
                <Select.Positioner>
                  <Select.Popup>
                    <Select.List />
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox.Root
              inputId="settings-vat-registered"
              checked={form.vatRegistered}
              onCheckedChange={(event: { checked: boolean }) =>
                setForm({
                  ...form,
                  vatRegistered: Boolean(event.checked),
                })
              }
            >
              <Checkbox.Box>
                <Checkbox.Indicator match="checked">
                  <i className="pi pi-check" />
                </Checkbox.Indicator>
              </Checkbox.Box>
            </Checkbox.Root>
            {t("settings.vatRegistered", {
              defaultValue: "VAT registered",
            })}
          </label>
          <MutationError error={save.error} />
          <div className="sm:col-span-2">
            <Button type="submit" loading={save.isPending || undefined}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </Section>
      <TaxRates />
    </div>
  );
}

export function TaxRates() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TaxRate | null>(null);
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["tax-rates"] });
  const save = useMutation({
    mutationFn: () =>
      editing
        ? apiPatch(`/settings/tax-rates/${editing.id}`, {
            name,
            rate,
            active: editing.active,
            isDefault: editing.isDefault,
          })
        : apiPost("/settings/tax-rates", {
            name,
            rate,
            active: true,
            isDefault: false,
          }),
    onSuccess: async () => {
      setEditing(null);
      setName("");
      setRate("");
      await refresh();
    },
  });
  const toggle = useMutation({
    mutationFn: (tax: TaxRate) =>
      apiPatch(`/settings/tax-rates/${tax.id}`, { active: !tax.active }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (tax: TaxRate) => apiDelete(`/settings/tax-rates/${tax.id}`),
    onSuccess: refresh,
  });
  const query = useQuery({
    queryKey: ["tax-rates"],
    queryFn: () =>
      apiGet<{ data: TaxRate[] }>("/settings/tax-rates").then(
        (response) => response.data,
      ),
  });
  const startEdit = (tax: TaxRate) => {
    setEditing(tax);
    setName(tax.name);
    setRate(tax.rate);
  };
  return (
    <Section title={t("settings.taxRates", { defaultValue: "Tax rates" })}>
      <div className="mb-5 divide-y divide-app-border rounded-md border border-app-border">
        {query.data?.map((tax) => (
          <div
            key={tax.id}
            className="flex flex-wrap items-center justify-between gap-3 p-3"
          >
            <div>
              <p className="font-medium text-app-text">
                {t(
                  `settings.taxRateNames.${DEFAULT_TAX_RATE_KEYS[tax.name] ?? "custom"}`,
                  { defaultValue: tax.name },
                )}
              </p>
              <p className="text-sm text-app-muted">
                {tax.rate}% ·{" "}
                {tax.active ? t("common.active") : t("common.inactive")}
                {tax.isDefault ? ` · ${t("common.default")}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="small"
                variant="outlined"
                severity="secondary"
                onClick={() => toggle.mutate(tax)}
              >
                {tax.active ? t("common.deactivate") : t("common.activate")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                severity="secondary"
                onClick={() => startEdit(tax)}
              >
                {t("common.edit")}
              </Button>
              {!tax.isDefault ? (
                <ConfirmButton
                  confirmMessage="Delete this tax rate?"
                  onConfirm={() => remove.mutate(tax)}
                >
                  {t("common.delete")}
                </ConfirmButton>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label={t("common.name", { defaultValue: "Name" })}>
          <Input
            required
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
          />
        </Field>
        <Field label={t("settings.rate", { defaultValue: "Rate %" })}>
          <InputNumber.Root
            fluid
            required
            min={0}
            step={0.1}
            value={rate ? Number(rate) : null}
            onValueChange={(event: { value: number | null }) =>
              setRate(event.value == null ? "" : String(event.value))
            }
          >
            <InputNumber.Input as={Input} />
          </InputNumber.Root>
        </Field>
        <Button type="submit" loading={save.isPending || undefined}>
          {editing ? t("common.save") : t("common.add")}
        </Button>
        {editing ? (
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={() => {
              setEditing(null);
              setName("");
              setRate("");
            }}
          >
            {t("common.cancel")}
          </Button>
        ) : null}
      </form>
      <MutationError error={save.error || toggle.error || remove.error} />
    </Section>
  );
}
